/**
 * Sizing views for the EVAR report: SVG sizing sheet + interactive 3D view.
 *
 * Rendered inline on page 1 when the device selection is short enough, or on
 * a dedicated page when a long device-selection table would squeeze them.
 */

import svgContent from '../../assets/AAA_Sizing_Sheet.svg?raw';
import { getMeasurementFromPlaceholder } from '../../domain/evar-svg-mapping';

import { ReportPage, useReportViewport } from '@/components/report';
import { SvgSizingSheet } from '@/workflows/vascular/common/components/svg-sizing-sheet';

export const EVAR_REPORT_VIEWPORT_ID = 'report-3d-view';

/**
 * Two-column band: SVG sizing sheet | standalone 3D viewport.
 * Flexible height — fills whatever vertical space the host page leaves.
 * Only one instance may be mounted at a time (single 3D viewport id).
 */
export function EvarReportSizingViews() {
    const { containerRef } = useReportViewport({ viewportId: EVAR_REPORT_VIEWPORT_ID });

    return (
        <div className="flex flex-1 gap-3 min-h-0">
            <div className="flex-1 min-h-0 overflow-hidden">
                <SvgSizingSheet
                    svgContent={svgContent}
                    getMeasurementFromPlaceholder={getMeasurementFromPlaceholder}
                    readOnly
                />
            </div>
            <div
                ref={containerRef}
                data-report-viewport
                className="relative flex-1 min-h-0 overflow-hidden rounded"
                style={{ backgroundColor: '#f5f5f4' }}
            />
        </div>
    );
}

interface EvarReportViewsPageProps {
    pageNumber: number;
    totalPages: number;
}

/**
 * Dedicated full page for the sizing views, used when the device-selection
 * table is too long to share page 1 with them.
 */
export function EvarReportViewsPage({ pageNumber, totalPages }: EvarReportViewsPageProps) {
    return (
        <ReportPage pageNumber={pageNumber} totalPages={totalPages}>
            <h2 className="mb-3 border-b border-gray-300 pb-1 text-sm font-semibold uppercase tracking-wide text-gray-700">
                Sizing Overview
            </h2>
            <EvarReportSizingViews />
        </ReportPage>
    );
}
